import { hash } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { ValidationErrors } from 'webapp-types';
import { equal, isAlive } from 'webapp/utils/common';
import { getPrettyModelName } from 'webapp/utils/model';
import { validateIp } from 'webapp/utils/validate';

import setField from 'webapp/helpers/set-field';
import BoardLoopsDelayInputComponent from 'webapp/components/board-loops/delay-input';
import type { Model } from 'webapp/components/common/share/link/index';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import FormInputComponent from 'webapp/components/form/input';
import FormPasswordWithCheckboxComponent from 'webapp/components/form/password-with-checkbox';
import RowErrorComponent from 'webapp/components/row-error';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import BoardLoop from 'webapp/models-custom/board-loop';

interface Args {
	model: Model;
	onSave: () => void;
}

export default class CommonShareLinkSettingsComponent extends Component<Args> {
	@tracked errors: ValidationErrors<'ips'> = {};

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const model = this.args.model,
			restrictIps = model.restrictIps;

		if (restrictIps.length) {
			this.ipRestrict = true;
			this.restrictIpsString = restrictIps.join(', ');
		}
	}

	get showLoopSettings(): boolean {
		return this.args.model instanceof BoardLoop;
	}

	get modelAsLoop() {
		return this.args.model as BoardLoop;
	}

	get modelTitle(): string {
		return getPrettyModelName(this.args.model);
	}

	// ----------- ip restrict ---------

	@tracked ipRestrict = false;
	@tracked restrictIpsString = '';

	get ipRestrictDisabled(): boolean {
		return !this.ipRestrict;
	}

	@action
	changeIpRestrict(ipRestrict: boolean): void {
		this.ipRestrict = ipRestrict;

		if (!ipRestrict) {
			this.args.model.restrictIps = [];
			this.restrictIpsString = '';

			this.save(); // only trigger on unselect (clearing property)
		}
	}

	// --------- anonymousEdit props ------

	get restrictAnonymousDateRangeEdit(): boolean {
		return !this.args.model.allowAnonymousDateRangeEdit;
	}

	@action
	changeRestrictAnonymousDateRangeEdit(value: boolean): void {
		this.args.model.allowAnonymousDateRangeEdit = !value;
		this.save();
	}

	get restrictAnonymousFilterEdit(): boolean {
		return !this.args.model.allowAnonymousFilterEdit;
	}

	@action
	changeRestrictAnonymousFilterEdit(value: boolean): void {
		this.args.model.allowAnonymousFilterEdit = !value;
		this.save();
	}

	// ---------- validation ---------
	validateForm(): boolean {
		const errors: ValidationErrors<'ips'> = {};
		const ipsValid = this.handleIpsFromString();

		// validate ips
		if (!ipsValid) {
			errors.ips = [{ message: 'Invalid IPs' }];
		}

		if (isAlive(this)) {
			// sometimes still need to validate and save after popup has been closed and component destroyed
			// (e.g. board removed from loop)
			this.errors = errors;
		}

		return Object.keys(errors).length === 0;
	}

	handleIpsFromString(): boolean {
		let isValid;
		const string = this.restrictIpsString,
			ips = [] as string[],
			rawIps = [] as string[];

		if (!this.ipRestrict) {
			return true;
		}

		string.split(',').forEach((ip) => {
			const trimmedIp = ip.trim();

			if (trimmedIp) {
				rawIps.push(trimmedIp);
			}
		});

		isValid = rawIps.length > 0;

		rawIps.forEach((ip) => {
			if (validateIp(ip)) {
				ips.push(ip);
			} else {
				isValid = false;
			}
		});

		if (isValid && !equal(this.args.model.restrictIps, ips)) {
			this.args.model.restrictIps = ips;
			this.restrictIpsString = ips.join(', ');
		}

		return isValid;
	}

	@action
	savePassword(password: string): void {
		this.args.model.password = password;
		this.save();
	}

	@action
	save(): void {
		if (this.validateForm()) {
			this.args.onSave();
		}
	}
	<template>
		{{#if this.showLoopSettings}}
			<BoardLoopsDelayInputComponent @model={{this.modelAsLoop}} @onChange={{this.save}} />
		{{/if}}

		<FormPasswordWithCheckboxComponent
			@label="Protect with password"
			@password={{@model.password}}
			@errors={{this.errors}}
			@onChange={{this.savePassword}}
		/>

		<div class="row ips">
			<div class="checkbox">
				<FormCheckboxComponent
					@name="ipRestrict"
					@selected={{this.ipRestrict}}
					@label="Limit to IP addresses:"
					@onChange={{this.changeIpRestrict}}
				/>
			</div>
			<div class="input">
				<FormInputComponent
					id="restrictIps"
					name="restrictIps"
					placeholder="Enter IP addresses"
					disabled={{this.ipRestrictDisabled}}
					@value={{this.restrictIpsString}}
					@focusOut={{this.save}}
					@onEmpty={{setField target=this property="restrictIpsString"}}
					@hideFloatLabel={{true}}
					@loseFocusOnEnter={{true}}
				/>
				<ToolTipWithIconComponent
					@text="Comma-separated list of multiple IPs,<br>that can access the {{this.modelTitle}}.<br>Wildcards are allowed (i.e. 193.42.*.*)"
					@appendTo="#restrictIps"
				/>
				<RowErrorComponent @items={{this.errors.ips}} />
			</div>
		</div>

		<div class="row restrict-anonymous-dr-edit">
			<FormCheckboxComponent
				@name="restrictAnonymousDateRangeEdit"
				@selected={{this.restrictAnonymousDateRangeEdit}}
				@label="Restrict ability to view additional date ranges for external viewers"
				@onChange={{this.changeRestrictAnonymousDateRangeEdit}}
				@item={{hash
					tooltip="By enabling this setting, shareable link viewers who are not logged into the Databox app will only be able to view the default date range on each datablock or for the whole dashboard."
				}}
			/>
		</div>

		<div class="row restrict-anonymous-f-edit">
			<FormCheckboxComponent
				@name="restrictAnonymousFilterEdit"
				@selected={{this.restrictAnonymousFilterEdit}}
				@label="Restrict ability to view additional filters for external viewers"
				@onChange={{this.changeRestrictAnonymousFilterEdit}}
				@item={{hash
					tooltip="By enabling this setting, shareable link viewers who are not logged into the Databox app will only be able to view filter options on each datablock or for the whole dashboard."
				}}
			/>
		</div>
	</template>
}
